import React from "react";
import {
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from "../common/sharedFunctions";
import {
  resolveDriverDisplayName,
  summarizeDispatchLog,
} from "../utils/dispatchLogSummary";

const outcomeLabel = (outcome, t) => {
  if (outcome === "delivered") {
    return t("dispatch_notify_delivered");
  }
  if (outcome === "failed") {
    return t("dispatch_notify_failed");
  }
  return t("dispatch_notify_sent");
};

function DriverTable({ rows, users, showOutcome, t }) {
  if (!rows.length) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 1, fontFamily: FONT_FAMILY }}
      >
        {t("dispatch_empty_list")}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
            {t("dispatch_col_driver")}
          </TableCell>
          <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
            {t("dispatch_col_platform")}
          </TableCell>
          {showOutcome ? (
            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
              {t("dispatch_col_status")}
            </TableCell>
          ) : (
            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
              {t("dispatch_col_distance")}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.driverUid}>
            <TableCell sx={{ fontFamily: FONT_FAMILY }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {resolveDriverDisplayName(row.driverUid, users)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.driverUid}
              </Typography>
            </TableCell>
            <TableCell sx={{ fontFamily: FONT_FAMILY }}>
              {row.platform || "—"}
            </TableCell>
            {showOutcome ? (
              <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                {outcomeLabel(row.outcome, t)}
                {row.detail ? (
                  <Typography variant="caption" display="block" color="error">
                    {row.detail}
                  </Typography>
                ) : null}
              </TableCell>
            ) : (
              <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                {row.distance != null
                  ? `${Number(row.distance).toFixed(2)} km`
                  : "—"}
                {row.timein_text ? ` · ${row.timein_text}` : ""}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function DispatchTrackingCard({
  logEntries,
  booking,
  users,
  cardClassName,
  titleClassName,
}) {
  const { t } = useTranslation();
  const { considered, notified } = summarizeDispatchLog(logEntries, booking);
  const hasLog = logEntries && logEntries.length > 0;
  const hasSnapshot =
    booking &&
    (booking.driverEstimates ||
      booking.requestedDrivers ||
      booking.preRequestedDrivers);

  if (!hasLog && !hasSnapshot) {
    return (
      <Card className={cardClassName}>
        <Typography className={titleClassName} variant="h5">
          {t("dispatch_tracking_title")}
        </Typography>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY }}>
            {t("dispatch_no_data")}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card className={cardClassName}>
      <Typography className={titleClassName} variant="h5">
        {t("dispatch_tracking_title")}
      </Typography>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold", mb: 1 }}
        >
          {t("dispatch_drivers_considered")} ({considered.length})
        </Typography>
        <DriverTable rows={considered} users={users} showOutcome={false} t={t} />

        <Typography
          variant="subtitle1"
          sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold", mt: 3, mb: 1 }}
        >
          {t("dispatch_drivers_notified")} (
          {notified.length})
        </Typography>
        <DriverTable rows={notified} users={users} showOutcome t={t} />
      </Box>
    </Card>
  );
}
